import { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { 
  Target, 
  TrendingUp, 
  BarChart3, 
  ArrowRight, 
  CheckCircle2, 
  Phone, 
  MessageCircle, 
  Mail,
  Search,
  Wrench,
  Rocket,
  RefreshCw,
  Quote,
  ChevronDown,
  ChevronUp,
  Zap,
  Users,
  Calendar,
  Menu,
  X,
  ShieldCheck,
  Server,
  XCircle,
  Globe
} from "lucide-react";

// Logo Costa Agency
const LOGO_URL = "https://customer-assets.emergentagent.com/job_system-costa/artifacts/1lcx3enf_Design%20sans%20titre%20%2814%29.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp — single source of truth
const WHATSAPP_NUMBER = "+41789227594";
const WHATSAPP_TEXT = "Bonjour, je veux un système d'acquisition pour mon entreprise";
const WHATSAPP_URL = `https://wa.me/41789227594?text=${encodeURIComponent(WHATSAPP_TEXT)}`;

const trackWhatsAppClick = (location) => {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "whatsapp_click", location });
  }
};

// Reusable green WhatsApp CTA
const WhatsAppCTA = ({ location, label = "Discuter sur WhatsApp", size = "md", className = "", testId }) => (
  <a
    href={WHATSAPP_URL}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => trackWhatsAppClick(location)}
    className={`btn-whatsapp ${size === "lg" ? "btn-lg" : ""} ${className}`}
    data-testid={testId || `whatsapp-cta-${location}`}
    aria-label="Démarrer une conversation WhatsApp avec Costa Agency"
  >
    <MessageCircle size={size === "lg" ? 24 : 20} />
    {label}
  </a>
);

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Reusable Section Component
const Section = ({ children, className = "", id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      id={id}
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={`section ${className}`}
    >
      {children}
    </motion.section>
  );
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const navLinks = [
    { id: "solution", label: "Notre approche" },
    { id: "process", label: "Processus" },
    { id: "offer", label: "Offre" },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0B0F2F]/95 backdrop-blur-lg border-b border-white/10 py-1" : "py-4 pointer-events-none"
      }`} role="navigation" aria-label="Navigation principale">
        <div className="container flex items-center justify-between">
          <a href="/" className={`flex items-center gap-3 transition-all duration-300 pointer-events-auto ${scrolled ? "" : "opacity-0"}`} data-testid="logo">
            <img src={LOGO_URL} alt="Costa Agency" className={`w-auto transition-all duration-300 ${scrolled ? "h-20 md:h-24" : "h-32 md:h-40"}`} />
          </a>
          
          {/* Desktop Nav */}
          <div className={`hidden md:flex items-center gap-8 transition-all duration-300 pointer-events-auto ${scrolled ? "opacity-100" : "opacity-0"}`}>
            {navLinks.map(link => (
              <button 
                key={link.id}
                onClick={() => scrollToSection(link.id)} 
                className="text-white/70 hover:text-white transition-colors text-sm font-medium"
                data-testid={`nav-${link.id}`}
              >
                {link.label}
              </button>
            ))}
            <a 
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("nav")}
              className="btn-whatsapp text-sm"
              style={{ padding: "10px 22px", fontSize: 14 }}
              data-testid="nav-contact-btn"
            >
              <MessageCircle size={16} />
              Discuter
            </a>
          </div>
          
          {/* Mobile Hamburger */}
          <button 
            className={`md:hidden p-2 text-white pointer-events-auto transition-all duration-300 ${scrolled ? "opacity-100" : "opacity-0"}`}
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Ouvrir le menu"
          >
            <Menu size={28} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-[#0B0F2F]/98 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <img src={LOGO_URL} alt="Costa Agency" className="h-36 w-auto" />
                <button 
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 text-white"
                  data-testid="mobile-menu-close"
                  aria-label="Fermer le menu"
                >
                  <X size={28} />
                </button>
              </div>
              
              {/* Nav Links */}
              <div className="flex-1 flex flex-col justify-center px-8 space-y-2">
                {navLinks.map((link, index) => (
                  <motion.button
                    key={link.id}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => scrollToSection(link.id)}
                    className="text-2xl font-heading font-semibold text-white py-4 text-left hover:text-costa-accent transition-colors border-b border-white/10"
                    data-testid={`mobile-nav-${link.id}`}
                  >
                    {link.label}
                  </motion.button>
                ))}
                <motion.button
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  onClick={() => scrollToSection("contact")}
                  className="text-2xl font-heading font-semibold text-costa-accent py-4 text-left"
                  data-testid="mobile-nav-contact"
                >
                  Contact
                </motion.button>
              </div>
              
              {/* Footer CTA */}
              <div className="p-8 border-t border-white/10">
                <motion.a
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { trackWhatsAppClick("mobile_menu"); setMobileMenuOpen(false); }}
                  className="btn-whatsapp w-full"
                  data-testid="mobile-cta"
                >
                  <MessageCircle size={20} />
                  Discuter sur WhatsApp
                </motion.a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToSolution = () => {
    const element = document.getElementById("solution");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="hero-bg min-h-screen flex items-center pt-4 md:pt-8 relative overflow-hidden">
      {/* Background orbs */}
      <div className="orb orb-1" />
      <div className="orb orb-2" />
      <div className="orb orb-3" />
      
      <div className="container relative z-10">
        {/* Logo at top of hero */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-2 flex justify-center md:justify-start overflow-hidden md:-ml-8"
        >
          <img src={LOGO_URL} alt="Costa Agency" className="h-44 sm:h-56 md:h-64 lg:h-80 w-auto object-contain scale-150 origin-center" />
        </motion.div>
        
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.span 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-costa-primary/20 border border-costa-primary/30 rounded-full px-4 py-2 text-sm text-costa-accent mb-6"
            >
              <Zap size={16} />
              Systèmes d'acquisition clients
            </motion.span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading leading-tight mb-6">
              Vos clients arrivent au hasard.{" "}
              <span className="text-gradient">On construit le système qui les fait venir en continu.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-costa-muted mb-4 font-medium">
              Mettez en place un système qui génère des clients de manière prévisible en 90 jours.
            </p>
            
            <p className="text-costa-muted text-lg mb-10 max-w-xl">
              Nous aidons les entreprises à attirer des prospects qualifiés et à les convertir en clients grâce à des systèmes d'acquisition performants.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <WhatsAppCTA location="hero" testId="hero-cta-primary" />
              <button 
                onClick={scrollToSolution}
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="hero-cta-secondary"
              >
                Voir comment ça fonctionne
                <ChevronDown size={20} />
              </button>
            </div>
          </motion.div>
          
          {/* Right: Dashboard Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="dashboard-mockup p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              
              {/* Dashboard content */}
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="stat-card">
                    <p className="text-costa-muted text-xs mb-1">Leads générés</p>
                    <p className="text-2xl font-bold text-white">+247%</p>
                  </div>
                  <div className="stat-card">
                    <p className="text-costa-muted text-xs mb-1">Taux conversion</p>
                    <p className="text-2xl font-bold text-costa-accent">12.8%</p>
                  </div>
                  <div className="stat-card">
                    <p className="text-costa-muted text-xs mb-1">ROI moyen</p>
                    <p className="text-2xl font-bold text-green-400">4.2x</p>
                  </div>
                </div>
                
                {/* Chart placeholder */}
                <div className="h-40 bg-white/5 rounded-lg flex items-end p-4 gap-2">
                  {[40, 65, 45, 80, 60, 95, 75, 85, 90, 100, 85, 110].map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      animate={{ height: `${h}%` }}
                      transition={{ delay: 0.5 + i * 0.05, duration: 0.5 }}
                      className="flex-1 bg-gradient-to-t from-costa-primary to-costa-accent rounded-t"
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Floating stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="absolute -bottom-6 -left-6 glass rounded-xl p-4 glow-sm"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-xs text-costa-muted">Croissance mensuelle</p>
                  <p className="text-lg font-bold text-white">+34%</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Floating Orb Component for reuse
const FloatingOrb = ({ className, delay = 0 }) => (
  <motion.div
    className={`absolute rounded-full filter blur-[80px] opacity-30 ${className}`}
    animate={{
      x: [0, 30, -20, 0],
      y: [0, -30, 20, 0],
    }}
    transition={{
      duration: 12,
      repeat: Infinity,
      ease: "easeInOut",
      delay
    }}
  />
);

// Problem Section
const ProblemSection = () => {
  const problems = [
    {
      icon: Users,
      title: "Vous dépendez du bouche-à-oreille",
      description: "Votre flux de clients est imprévisible et difficile à contrôler."
    },
    {
      icon: BarChart3,
      title: "Vos résultats sont irréguliers",
      description: "Un mois c'est excellent, le suivant c'est le calme plat."
    },
    {
      icon: Target,
      title: "Vous testez sans stratégie",
      description: "Vous essayez des choses au hasard sans vraie direction."
    },
    {
      icon: TrendingUp,
      title: "Vous investissez à l'aveugle",
      description: "Vous dépensez sans savoir ce qui fonctionne vraiment."
    }
  ];

  return (
    <Section id="problem" className="bg-[#080c24] relative overflow-hidden">
      {/* Background orbs */}
      <FloatingOrb className="w-[300px] h-[300px] bg-costa-primary top-20 -right-20" delay={0} />
      <FloatingOrb className="w-[200px] h-[200px] bg-costa-secondary bottom-20 left-10" delay={2} />
      
      <div className="container relative z-10">
        <motion.div variants={fadeInUp} className="max-w-3xl mb-16">
          <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6">
            Pourquoi votre croissance est{" "}
            <span className="text-costa-accent">Instable</span>
          </h2>
          <p className="text-costa-muted text-lg">
            Beaucoup d'entreprises font face aux mêmes défis. Ces situations vous parlent ?
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="card-hover glass rounded-2xl p-8 flex gap-5"
            >
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-costa-primary/20 flex items-center justify-center">
                  <problem.icon className="text-costa-accent" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold font-heading mb-2 text-white">
                  {problem.title}
                </h3>
                <p className="text-costa-muted">
                  {problem.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

// Transition Section
const TransitionSection = () => {
  return (
    <Section className="py-20 md:py-32">
      <div className="container">
        <motion.div 
          variants={fadeInUp}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="glass rounded-3xl p-12 md:p-16 glow">
            <Quote className="text-costa-accent/30 w-16 h-16 mx-auto mb-6" />
            <p className="text-2xl md:text-4xl font-heading font-semibold leading-relaxed mb-4">
              La plupart des entreprises n'ont pas un problème de marketing.
            </p>
            <p className="text-2xl md:text-4xl font-heading font-bold text-costa-accent">
              Elles n'ont pas de système.
            </p>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

// Solution Section
const SolutionSection = () => {
  const solutions = [
    {
      icon: Target,
      title: "Attirer",
      subtitle: "Les bonnes personnes",
      description: "Publicité ciblée pour toucher votre audience idéale."
    },
    {
      icon: RefreshCw,
      title: "Convertir",
      subtitle: "En clients",
      description: "Pages et tunnels optimisés pour transformer les visiteurs."
    },
    {
      icon: BarChart3,
      title: "Mesurer",
      subtitle: "Et optimiser",
      description: "Tracking et data pour améliorer en continu."
    }
  ];

  return (
    <Section id="solution" className="bg-[#080c24]">
      <div className="container">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-costa-accent text-sm uppercase tracking-widest font-medium mb-4 block">
            Notre approche
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6">
            Un système d'acquisition pensé pour{" "}
            <span className="text-gradient">générer des clients</span>
          </h2>
          <p className="text-costa-muted text-lg mb-2">
            Pas des services à la carte. Un système complet et structuré.
          </p>
          <p className="text-costa-muted/70 text-sm">
            Basés à Genève, nous accompagnons des entreprises partout en Suisse.
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="card-hover glass rounded-2xl p-8 text-center relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-costa-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-costa-primary/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <solution.icon className="text-costa-accent" size={32} />
                </div>
                <h3 className="text-2xl font-bold font-heading text-white mb-2">
                  {solution.title}
                </h3>
                <p className="text-costa-accent font-medium mb-4">
                  {solution.subtitle}
                </p>
                <p className="text-costa-muted">
                  {solution.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Analyse",
      description: "Compréhension approfondie de votre business, vos objectifs et votre marché.",
      icon: Search
    },
    {
      number: "02",
      title: "Construction",
      description: "Mise en place du système complet : tunnels, pages, tracking.",
      icon: Wrench
    },
    {
      number: "03",
      title: "Lancement",
      description: "Activation des campagnes publicitaires et début de l'acquisition.",
      icon: Rocket
    },
    {
      number: "04",
      title: "Optimisation",
      description: "Amélioration continue basée sur les données et les résultats.",
      icon: RefreshCw
    }
  ];

  return (
    <Section id="process">
      <div className="container">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-20">
          <span className="text-costa-accent text-sm uppercase tracking-widest font-medium mb-4 block">
            Comment ça marche
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading">
            Un processus en <span className="text-costa-accent">4 étapes</span>
          </h2>
        </motion.div>
        
        <div className="relative">
          {/* Timeline line for desktop */}
          <div className="hidden md:block process-line" />
          
          <div className="space-y-12 md:space-y-0">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className={`relative md:w-1/2 ${
                  index % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16 md:ml-auto"
                }`}
              >
                <div className={`glass rounded-2xl p-8 card-hover ${
                  index % 2 === 0 ? "md:mr-8" : "md:ml-8"
                }`}>
                  <div className={`flex items-start gap-4 ${
                    index % 2 === 0 ? "md:flex-row-reverse" : ""
                  }`}>
                    <div className="number-badge flex-shrink-0">
                      {step.number}
                    </div>
                    <div className={index % 2 === 0 ? "md:text-right" : ""}>
                      <h3 className="text-2xl font-bold font-heading text-white mb-2 flex items-center gap-3">
                        {index % 2 !== 0 && <step.icon className="text-costa-accent hidden md:block" size={24} />}
                        {step.title}
                        {index % 2 === 0 && <step.icon className="text-costa-accent hidden md:block" size={24} />}
                      </h3>
                      <p className="text-costa-muted">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Timeline dot - hidden */}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

// Social Proof Section
const ProofSection = () => {
  const testimonials = [
    {
      quote: "J'ai fait appel à Costa Agency pour lancer une campagne Google Ads à l'ouverture de mon restaurant, et honnêtement ça a vraiment fait la différence. Assez vite, j'ai commencé à voir de nouveaux clients arriver. L'équipe est super pro, à l'écoute, et surtout très orientée résultats. Je recommande sans hésiter !",
      author: "Victoria Ritter",
      role: "Fondatrice, Cactus Cafe",
      logo: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/huwo0ixf_Untitled%2Bdesign-3.webp"
    },
    {
      quote: "Nous avons fait appel à leurs services pour notre entreprise de nettoyage, Irisnet. Nous avons eu affaire à une équipe très professionnelle et dynamique pour le référencement de notre site Internet avec en plus des résultats à la clef. Je recommande.",
      author: "Gina Arminio",
      role: "Directrice, Irisnet",
      logo: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/d644fvlu_Irisnet-01-1%20%282%29.png"
    },
    {
      quote: "En tant que marque établie et exigeante dans le domaine de la beauté et du soin capillaire, nous sommes particulièrement attentifs à la qualité de nos partenaires. Notre collaboration avec Costa Agency s'est inscrite dans cette logique. Nous avons particulièrement apprécié le niveau d'exigence, la rigueur et la compréhension fine de nos enjeux business. Une collaboration à la hauteur de nos standards, que nous recommandons.",
      author: "Mahefa ANDRIMANAMPISOA",
      role: "Fondateur, SmoothLabs",
      logo: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/p3dkcvkm_2024122413-3aa4069b-f21e-4d84-b5e4-1fa7daca6d09-new%20wordmark%20-%20smooth%20labs%20logo.png"
    }
  ];

  const clientLogos = [
    { name: "Cactus", url: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/huwo0ixf_Untitled%2Bdesign-3.webp" },
    { name: "Irisnet", url: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/d644fvlu_Irisnet-01-1%20%282%29.png" },
    { name: "SmoothLabs", url: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/p3dkcvkm_2024122413-3aa4069b-f21e-4d84-b5e4-1fa7daca6d09-new%20wordmark%20-%20smooth%20labs%20logo.png" },
    { name: "ACASA", url: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/wd2kcegq_Logo%20ACASA%20%281%29.jpg" },
    { name: "Comptarial", url: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/p0uga2p8_Compta_LOGO%20%281%29.png" },
    { name: "Coiffure Sula", url: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/1334rw8u_130686765_2891483507554750_4058264428030295240_n%20%281%29.jpg" },
    { name: "Lb Rental", url: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/vh6wy2jy_image.png" },
    { name: "Festh", url: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/f9w36jzd_Sans%20titre.png" },
    { name: "Vortex Padel", url: "https://customer-assets.emergentagent.com/job_system-costa/artifacts/3nhxxfi9_Logo_Vortex_Padel_original_sans_fond.webp" }
  ];

  // Duplicate logos for seamless scroll
  const duplicatedLogos = [...clientLogos, ...clientLogos, ...clientLogos, ...clientLogos];

  return (
    <Section id="proof" className="bg-[#080c24]">
      <div className="container">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-costa-accent text-sm uppercase tracking-widest font-medium mb-4 block">
            Ils nous font confiance
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading mb-8">
            Des résultats <span className="text-costa-accent">Concrets</span>
          </h2>
        </motion.div>
        
        {/* Scrolling Client Logos */}
        <motion.div variants={fadeInUp} className="relative overflow-hidden mb-16 py-6 bg-white rounded-xl mx-4">
          {/* Gradient overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10" />
          
          {/* Scrolling track */}
          <div className="flex items-center gap-16 animate-scroll-logos">
            {duplicatedLogos.map((logo, i) => (
              <div key={i} className="flex-shrink-0 h-16 w-36 flex items-center justify-center">
                <img 
                  src={logo.url} 
                  alt={logo.name} 
                  className="max-h-16 max-w-36 object-contain"
                />
              </div>
            ))}
          </div>
        </motion.div>
        
        {/* Testimonials */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-white rounded-2xl p-8 flex flex-col shadow-lg"
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                ))}
              </div>
              
              <Quote className="text-costa-primary/20 w-8 h-8 mb-3" />
              <p className="text-gray-700 text-sm leading-relaxed mb-6 flex-grow">
                {testimonial.quote}
              </p>
              <div className="flex items-center gap-4 mt-auto">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center p-2">
                  <img src={testimonial.logo} alt={testimonial.author} className="w-full h-full object-contain" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-gray-500 text-sm">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

// Offer Section
const OfferSection = () => {
  const features = [
    "Mise en place complète du système",
    "Page \"La Ligne Directe\" vers WhatsApp",
    "Campagnes publicitaires optimisées",
    "Pages de conversion performantes",
    "Accompagnement à la création de contenu organique",
    "Suivi et optimisation continue",
    "Reporting hebdomadaire",
    "Support dédié"
  ];

  return (
    <Section id="offer">
      <div className="container">
        <div className="glass rounded-3xl p-8 md:p-16 glow relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-costa-primary/10 to-transparent pointer-events-none" />
          
          <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInUp}>
              <span className="text-costa-accent text-sm uppercase tracking-widest font-medium mb-4 block">
                Notre offre
              </span>
              <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6">
                Votre système d'acquisition —{" "}
                <span className="text-costa-accent">conçu, lancé et optimisé en 90 jours</span>
              </h2>
              <p className="text-costa-muted text-lg mb-8">
                On prend en charge la construction complète de votre système — vous vous concentrez sur votre business.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="text-costa-accent flex-shrink-0" size={20} />
                    <span className="text-white/90">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="lg:text-center">
              <div className="bg-[#0B0F2F] rounded-2xl p-8 border border-white/10">
                <div className="flex items-center justify-center gap-2 mb-6">
                  <Calendar className="text-costa-accent" size={24} />
                  <span className="text-costa-muted">Accompagnement 90 jours</span>
                </div>
                <p className="text-costa-muted mb-8">
                  Une question, un projet ? Démarrez la conversation directement sur WhatsApp.
                </p>
                <div className="flex justify-center">
                  <WhatsAppCTA location="offer" testId="offer-cta" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <Section className="bg-[#080c24] py-20">
      <div className="container">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6">
            Et si votre acquisition devenait enfin{" "}
            <span className="text-gradient">prévisible</span> ?
          </h2>
          <p className="text-costa-muted text-lg mb-10">
            Discutons de votre situation et voyons comment nous pouvons vous aider.
          </p>
          <div className="flex justify-center">
            <WhatsAppCTA location="cta_final" size="lg" testId="cta-primary" />
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <Section id="contact" className="relative overflow-hidden">
      {/* Background orbs */}
      <FloatingOrb className="w-[350px] h-[350px] bg-costa-secondary -top-20 right-0" delay={1} />
      <FloatingOrb className="w-[250px] h-[250px] bg-costa-accent bottom-10 -left-20" delay={3} />
      
      <div className="container relative z-10">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto">
          <span className="text-costa-accent text-sm uppercase tracking-widest font-medium mb-4 block">
            Contact
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6">
            Parlons de votre <span className="text-costa-accent">projet</span>
          </h2>
          <p className="text-costa-muted text-lg mb-10">
            Le plus rapide : un message WhatsApp. Vous êtes en conversation avec nous en quelques secondes.
          </p>

          {/* WhatsApp — seul point de contact */}
          <div className="glass rounded-3xl p-8 md:p-10 border border-[#25D366]/30 max-w-2xl mx-auto" data-testid="contact-whatsapp-hero">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MessageCircle className="text-[#25D366]" size={28} />
              <span className="text-white font-heading font-semibold text-lg">WhatsApp · {WHATSAPP_NUMBER}</span>
            </div>
            <p className="text-costa-muted mb-6">Réponse rapide, du lundi au vendredi.</p>
            <div className="flex justify-center">
              <WhatsAppCTA location="contact_main" size="lg" />
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Qu'est-ce que La Ligne Directe ?",
      answer: "La Ligne Directe, c'est notre méthode signature : on remplace les formulaires et les rappels par une page sans friction avec un seul bouton — WhatsApp. Votre prospect passe de la publicité à une conversation avec vous en un clic. Zéro étape inutile, zéro lead perdu en route."
    },
    {
      question: "Proposez-vous une garantie de résultats ?",
      answer: "Oui. Si à l'issue des 90 jours le système n'est pas opérationnel et n'a pas généré de demandes qualifiées, on continue gratuitement jusqu'à ce qu'il le soit. Le système reste in-house chez vous : compte publicitaire, page, accès — tout vous appartient, sans dépendance à l'agence."
    },
    {
      question: "Combien de temps avant de voir des résultats ?",
      answer: "La majorité de nos clients voient leurs premiers leads qualifiés dans les 30 premiers jours. Notre engagement : un système opérationnel, lancé et optimisé en 90 jours maximum."
    },
    {
      question: "Pourquoi un système plutôt que des prestations à la carte ?",
      answer: "Une prestation isolée (juste une pub, juste une page) ne génère pas de croissance prévisible. Un système connecte chaque étape — attirer, convertir, mesurer — pour créer un flux de clients récurrent que vous contrôlez."
    },
    {
      question: "À qui s'adresse cet accompagnement ?",
      answer: "Aux entreprises B2B et B2C qui ont déjà une offre validée mais qui dépendent du bouche-à-oreille ou de canaux instables. Si vous voulez sortir du chaos et installer une croissance maîtrisée, on peut vous aider."
    },
    {
      question: "Travaillez-vous avec mon secteur ?",
      answer: "Nous travaillons avec les prestataires de services à haute valeur ajoutée — BTP, fiduciaires, entreprises de services, conseil, immobilier, professions libérales — où le client doit passer par une consultation ou une discussion avec le patron (ou un référent) avant de signer. C'est exactement là que La Ligne Directe fait la différence : on amène un prospect chaud, directement en conversation avec vous, prêt à parler de son projet."
    },
    {
      question: "Que devient le système après les 90 jours ?",
      answer: "Au terme des 90 jours, vous repartez avec un système complet, documenté et fonctionnel : votre tunnel de conversion, vos pages, votre tracking et votre playbook d'acquisition. Les campagnes publicitaires s'arrêtent à la fin de l'accompagnement, mais l'infrastructure reste la vôtre. La majorité de nos clients choisissent de prolonger pour continuer à diffuser et optimiser leurs campagnes — ce n'est pas une obligation."
    },
    {
      question: "Comment se déroule l'audit gratuit ?",
      answer: "Un appel de 30 minutes pendant lequel on analyse votre situation actuelle, on identifie les 3 leviers prioritaires et on vous remet un plan d'action concret — que vous décidiez ou non de travailler avec nous."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <Section id="faq" className="bg-[#080c24]">
      <div className="container">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-costa-accent text-sm uppercase tracking-widest font-medium mb-4 block">
            FAQ
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6">
            Les questions que vous vous{" "}
            <span className="text-costa-accent">posez</span>
          </h2>
          <p className="text-costa-muted text-lg">
            Tout ce qu'il faut savoir avant de démarrer.
          </p>
        </motion.div>

        <motion.div variants={fadeInUp} className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="glass rounded-xl overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                aria-expanded={openIndex === index}
              >
                <span className="font-semibold text-white pr-4">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="text-costa-accent flex-shrink-0" size={24} />
                ) : (
                  <ChevronDown className="text-costa-accent flex-shrink-0" size={24} />
                )}
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="mx-6 border-t border-costa-accent/20"></div>
                    <p className="px-6 pt-5 pb-6 text-costa-muted leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

// Footer
const Footer = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="py-16 border-t border-white/10 bg-[#060920]" role="contentinfo">
      <div className="container">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Left - Logo + Description */}
          <div>
            <img src={LOGO_URL} alt="Costa Agency" className="h-20 w-auto mb-4" />
            <p className="text-costa-muted text-sm leading-relaxed">
              Costa Agency — Agence d'acquisition client basée à Genève, accompagnant les entreprises suisses dans la mise en place de systèmes marketing prévisibles.
            </p>
          </div>

          {/* Center - Navigation */}
          <div>
            <h4 className="text-white font-semibold mb-4">Liens utiles</h4>
            <nav aria-label="Liens de navigation footer" className="space-y-2">
              <button onClick={() => scrollToSection("solution")} className="block text-costa-muted hover:text-white transition-colors text-sm">
                Notre approche
              </button>
              <button onClick={() => scrollToSection("process")} className="block text-costa-muted hover:text-white transition-colors text-sm">
                Processus
              </button>
              <button onClick={() => scrollToSection("offer")} className="block text-costa-muted hover:text-white transition-colors text-sm">
                Offre
              </button>
              <button onClick={() => scrollToSection("contact")} className="block text-costa-muted hover:text-white transition-colors text-sm">
                Contact
              </button>
            </nav>
          </div>

          {/* Right - Legal + Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Légal & Contact</h4>
            <nav aria-label="Liens légaux" className="space-y-2 mb-4">
              <Link to="/mentions-legales" className="block text-costa-muted hover:text-white transition-colors text-sm">
                Mentions légales
              </Link>
              <Link to="/politique-de-confidentialite" className="block text-costa-muted hover:text-white transition-colors text-sm">
                Politique de confidentialité
              </Link>
            </nav>
            <div className="space-y-2">
              <a href="mailto:contact@costa-agency.ch" className="block text-costa-muted hover:text-white transition-colors text-sm">
                contact@costa-agency.ch
              </a>
              <a href="https://wa.me/41789227594" target="_blank" rel="noopener noreferrer" className="block text-costa-muted hover:text-white transition-colors text-sm">
                WhatsApp : +41 78 922 75 94
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 text-center">
          <p className="text-costa-muted text-sm">
            © {new Date().getFullYear()} Costa Agency. Tous droits réservés. — Basée à Genève, Suisse.
          </p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackWhatsAppClick("floating")}
      className="whatsapp-float"
      aria-label="Contactez-nous sur WhatsApp"
      data-testid="whatsapp-float"
    >
      <MessageCircle className="text-white" size={28} />
    </a>
  );
};

// Mentions Légales Page
const MentionsLegales = () => {
  return (
    <div className="min-h-screen bg-[#0B0F2F] pt-32 pb-20">
      <div className="container max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-costa-accent hover:text-white transition-colors mb-8">
          <ArrowRight className="rotate-180" size={20} />
          Retour à l'accueil
        </Link>
        
        <h1 className="text-4xl md:text-5xl font-bold font-heading mb-12 text-white">
          Mentions Légales
        </h1>

        <div className="space-y-8 text-costa-muted">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Éditeur du site</h2>
            <p className="leading-relaxed">
              Le site costa-agency.ch est édité par Costa Agency, basée à Genève, Suisse.
            </p>
            <p className="mt-4">
              <strong className="text-white">Contact :</strong><br />
              Téléphone / WhatsApp : +41 78 922 75 94<br />
              Email : contact@costa-agency.ch
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Hébergement</h2>
            <p className="leading-relaxed">
              Le site est hébergé par Emergent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Propriété intellectuelle</h2>
            <p className="leading-relaxed">
              L'ensemble des éléments présents sur ce site (textes, images, logo, charte graphique, structure) est la propriété exclusive de Costa Agency, sauf mention contraire. Toute reproduction, représentation, modification ou diffusion totale ou partielle sans autorisation écrite préalable est strictement interdite et constitue une contrefaçon sanctionnée par le Code Pénal Suisse.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Responsabilité</h2>
            <p className="leading-relaxed">
              Costa Agency met tout en œuvre pour fournir des informations exactes et à jour sur ce site. Néanmoins, Costa Agency ne saurait être tenue responsable des erreurs ou omissions, ni de l'utilisation faite des informations diffusées.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Droit applicable</h2>
            <p className="leading-relaxed">
              Le présent site est soumis au droit suisse. Tout litige relatif à son utilisation relève de la compétence exclusive des tribunaux de Genève.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Politique de Confidentialité Page
const PolitiqueConfidentialite = () => {
  return (
    <div className="min-h-screen bg-[#0B0F2F] pt-32 pb-20">
      <div className="container max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-costa-accent hover:text-white transition-colors mb-8">
          <ArrowRight className="rotate-180" size={20} />
          Retour à l'accueil
        </Link>
        
        <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4 text-white">
          Politique de Confidentialité
        </h1>
        <p className="text-costa-accent mb-12">
          Conforme à la nouvelle Loi suisse sur la Protection des Données (nLPD) et au RGPD.
        </p>

        <div className="space-y-8 text-costa-muted">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Responsable du traitement</h2>
            <p className="leading-relaxed">
              Costa Agency, basée à Genève, est responsable du traitement des données personnelles collectées via le site costa-agency.ch.<br />
              Contact : contact@costa-agency.ch — +41 78 922 75 94
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Données collectées</h2>
            <p className="leading-relaxed">Nous collectons les données suivantes :</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Données saisies dans le formulaire de contact : nom, email, téléphone, message</li>
              <li>Données de navigation via Google Analytics 4 et Google Tag Manager (anonymisées)</li>
              <li>Cookies techniques nécessaires au bon fonctionnement du site</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Finalités du traitement</h2>
            <p className="leading-relaxed">Vos données sont utilisées pour :</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Répondre à votre demande de contact ou d'audit gratuit</li>
              <li>Améliorer notre site et nos services (analyse de l'audience)</li>
              <li>Vous recontacter dans le cadre commercial si vous en avez fait la demande</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Base légale</h2>
            <p className="leading-relaxed">
              Le traitement est fondé sur votre consentement (formulaire de contact) et sur l'intérêt légitime de Costa Agency à analyser le trafic de son site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Durée de conservation</h2>
            <p className="leading-relaxed">
              Les données du formulaire sont conservées 3 ans après le dernier contact. Les données analytiques sont conservées 14 mois maximum.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Destinataires</h2>
            <p className="leading-relaxed">
              Vos données ne sont jamais vendues. Elles peuvent être traitées par nos sous-traitants techniques (Google Analytics, Google Tag Manager, Emergent pour l'hébergement). Tous sont engagés par un contrat de confidentialité.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Vos droits</h2>
            <p className="leading-relaxed">
              Conformément à la nLPD et au RGPD, vous disposez d'un droit d'accès, de rectification, de suppression, d'opposition et de portabilité de vos données. Pour exercer ces droits : contact@costa-agency.ch
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Cookies</h2>
            <p className="leading-relaxed">
              Notre site utilise des cookies techniques et des cookies de mesure d'audience (Google Analytics 4). Vous pouvez les désactiver dans les paramètres de votre navigateur.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Sécurité</h2>
            <p className="leading-relaxed">
              Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données contre tout accès non autorisé.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Modifications</h2>
            <p className="leading-relaxed">
              Cette politique peut être modifiée. La date de dernière mise à jour est indiquée ci-dessous.
            </p>
            <p className="mt-4 text-white font-medium">
              Dernière mise à jour : avril 2026
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// La Ligne Directe — méthode signature
const LigneDirecteSection = () => {
  const removed = [
    "Pas de formulaire à remplir",
    "Pas de menu qui disperse",
    "Pas de \"on vous rappellera\"",
    "Aucune étape inutile",
  ];

  const journey = [
    { num: "01", text: "Votre client vous cherche sur Google" },
    { num: "02", text: "Il clique sur votre annonce, ciblée sur votre zone" },
    { num: "03", text: "Il arrive sur une page sans distraction, un seul bouton : vous parler" },
    { num: "04", text: "Il ouvre une conversation WhatsApp avec vous, à chaud" },
  ];

  return (
    <Section id="ligne-directe" className="bg-[#080c24] relative overflow-hidden">
      <FloatingOrb className="w-[400px] h-[400px] bg-costa-primary -top-32 -right-32" delay={0.5} />
      <div className="container relative z-10">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-costa-accent text-sm uppercase tracking-widest font-medium mb-4 block">
            Notre méthode signature
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6">
            La Ligne Directe,{" "}
            <span className="text-gradient">notre méthode</span>
          </h2>
          <p className="text-costa-muted text-lg leading-relaxed">
            Les autres agences envoient vos prospects remplir un formulaire et attendre un rappel. La moitié se perd en route.<br className="hidden md:block" />
            Nous, on construit une page sans friction qui n'a qu'un seul but : vous mettre directement en conversation WhatsApp avec votre futur client.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto mb-12">
          {/* Ce qu'on enlève */}
          <motion.div variants={fadeInUp} className="glass rounded-2xl p-8 border border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center">
                <XCircle className="text-red-400" size={22} />
              </div>
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-white">Ce qu'on enlève</h3>
            </div>
            <ul className="space-y-3" data-testid="ligne-directe-removed">
              {removed.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-costa-muted">
                  <span className="text-red-400/80 mt-1">—</span>
                  <span className="line-through decoration-red-400/40 decoration-1">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Le parcours */}
          <motion.div variants={fadeInUp} className="glass rounded-2xl p-8 border border-costa-accent/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-costa-primary/20 flex items-center justify-center">
                <ArrowRight className="text-costa-accent" size={22} />
              </div>
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-white">Le parcours</h3>
            </div>
            <ol className="space-y-4" data-testid="ligne-directe-journey">
              {journey.map((step, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="text-costa-accent font-heading font-bold text-sm w-8 flex-shrink-0 mt-0.5">{step.num}</span>
                  <span className="text-white/90 leading-relaxed">{step.text}</span>
                </li>
              ))}
            </ol>
          </motion.div>
        </div>

        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto">
          <p className="text-xl md:text-2xl font-heading text-white mb-2">
            Il passe de <span className="text-costa-muted">"je découvre"</span> à <span className="text-costa-accent">"je parle au patron"</span> en un clic.
          </p>
          <p className="text-costa-muted mb-8">Zéro friction, zéro lead perdu.</p>
          <WhatsAppCTA location="ligne_directe" size="lg" />
        </motion.div>
      </div>
    </Section>
  );
};

// Études de cas
const CaseStudiesSection = () => {
  return (
    <Section id="case-studies" className="bg-[#080c24]">
      <div className="container">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-costa-accent text-sm uppercase tracking-widest font-medium mb-4 block">
            Études de cas
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6">
            Des résultats concrets,{" "}
            <span className="text-gradient">pas des promesses</span>
          </h2>
        </motion.div>

        {/* Carte 1 — featured */}
        <motion.div
          variants={fadeInUp}
          className="glass rounded-3xl p-6 md:p-10 mb-8 border border-costa-accent/20 glow"
          data-testid="case-study-1"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-block text-xs uppercase tracking-widest text-costa-accent bg-costa-primary/20 border border-costa-primary/30 rounded-full px-3 py-1 mb-4">
                Cas mis en avant
              </span>
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                Entreprise de nettoyage à Genève
              </h3>
              <p className="text-costa-muted leading-relaxed mb-4">
                Au départ : un agenda rempli au bouche-à-oreille, des semaines vides, des revenus instables.
              </p>
              <p className="text-white/90 leading-relaxed mb-6">
                Avec La Ligne Directe, son planning est passé de quasi vide à <span className="text-costa-accent font-semibold">×3,7 de remplissage</span> au deuxième mois.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white/80">Avant : semaines vides</span>
                <span className="bg-costa-accent/15 border border-costa-accent/30 rounded-full px-4 py-2 text-sm text-costa-accent font-medium">Après : ×3,7 au M2</span>
              </div>
            </div>
            {/* IMAGE A — agenda avant / après */}
            <div className="grid grid-cols-2 gap-3" data-image-slot="A">
              <figure className="bg-white/5 border border-white/10 rounded-xl p-2 overflow-hidden">
                <img
                  src="/case-agenda-avant.png"
                  alt="Agenda avant La Ligne Directe — semaines vides"
                  className="w-full h-auto rounded-lg"
                  loading="lazy"
                />
                <figcaption className="text-xs text-center text-costa-muted mt-2">Avant</figcaption>
              </figure>
              <figure className="bg-white/5 border border-costa-accent/30 rounded-xl p-2 overflow-hidden">
                <img
                  src="/case-agenda-apres.png"
                  alt="Agenda après La Ligne Directe — planning rempli"
                  className="w-full h-auto rounded-lg"
                  loading="lazy"
                />
                <figcaption className="text-xs text-center text-costa-accent mt-2">Après</figcaption>
              </figure>
            </div>
          </div>
        </motion.div>

        {/* Carte 3 — Témoignage client + capture WhatsApp (mise en forme identique à Carte 1) */}
        <motion.div
          variants={fadeInUp}
          className="glass rounded-3xl p-6 md:p-10 border border-[#25D366]/25"
          data-testid="case-study-3"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-block text-xs uppercase tracking-widest text-[#25D366] bg-[#25D366]/10 border border-[#25D366]/30 rounded-full px-3 py-1 mb-4">
                Témoignage client
              </span>
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                C'est nos clients qui le disent
              </h3>
              <p className="text-costa-muted leading-relaxed mb-4">
                Voilà les résultats qu'on apporte à nos clients en très peu de temps. Pas un argumentaire d'agence — un message reçu directement de l'un d'eux.
              </p>
              <p className="text-white/90 leading-relaxed mb-6">
                Sur un seul mois : <span className="text-[#25D366] font-semibold">+57% d'appels</span>, <span className="text-[#25D366] font-semibold">+64% de demandes d'itinéraire</span>, <span className="text-[#25D366] font-semibold">+88% d'affichages</span>.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-full px-4 py-2 text-sm text-[#25D366] font-medium">+57% appels</span>
                <span className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-full px-4 py-2 text-sm text-[#25D366] font-medium">+64% itinéraires</span>
                <span className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-full px-4 py-2 text-sm text-[#25D366] font-medium">+88% affichages</span>
              </div>
            </div>
            {/* IMAGE C — conversation WhatsApp */}
            <figure
              className="rounded-xl overflow-hidden border border-[#25D366]/30 bg-black/40 max-w-sm mx-auto lg:mx-0"
              data-image-slot="C"
            >
              <img
                src="/case-whatsapp.png"
                alt="Conversation WhatsApp d'un client partageant ses statistiques (+57% appels, +64% itinéraires, +88% affichages)"
                className="w-full h-auto block"
                loading="lazy"
              />
            </figure>
          </div>
        </motion.div>

        <motion.div variants={fadeInUp} className="text-center mt-12">
          <WhatsAppCTA location="case_studies" />
        </motion.div>
      </div>
    </Section>
  );
};

// In-House & Garantie
const InHouseSection = () => {
  return (
    <Section id="in-house" className="relative overflow-hidden">
      <div className="container">
        <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-costa-accent text-sm uppercase tracking-widest font-medium mb-4 block">
            In-House & Garantie
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading mb-6">
            Le système vous appartient,{" "}
            <span className="text-gradient">et il est garanti</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <motion.div variants={fadeInUp} className="glass rounded-2xl p-8 border border-white/10" data-testid="inhouse-card">
            <div className="w-14 h-14 rounded-2xl bg-costa-primary/20 flex items-center justify-center mb-5">
              <Server className="text-costa-accent" size={28} />
            </div>
            <h3 className="text-2xl font-heading font-bold text-white mb-3">100% in-house, chez vous</h3>
            <p className="text-costa-muted leading-relaxed">
              On construit tout chez vous. Compte publicitaire, page, vos accès. Le jour où on s'arrête, vous gardez l'intégralité du système. <span className="text-white font-medium">Zéro dépendance à l'agence.</span>
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="glass rounded-2xl p-8 border border-costa-accent/25" data-testid="guarantee-card">
            <div className="w-14 h-14 rounded-2xl bg-costa-accent/15 flex items-center justify-center mb-5">
              <ShieldCheck className="text-costa-accent" size={28} />
            </div>
            <h3 className="text-2xl font-heading font-bold text-white mb-3">Garantie résultats</h3>
            <p className="text-costa-muted leading-relaxed">
              Si à l'issue des 90 jours le système n'est pas opérationnel et n'a pas généré de demandes qualifiées, on continue gratuitement jusqu'à ce qu'il le soit. Votre risque est nul.
            </p>
          </motion.div>
        </div>
      </div>
    </Section>
  );
};

// Sticky mobile WhatsApp bar — removed (only floating button kept)

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Home Page
const HomePage = () => {
  return (
    <>
      <Navigation />
      <main id="main-content" role="main">
        <HeroSection />
        <ProblemSection />
        <TransitionSection />
        <LigneDirecteSection />
        <SolutionSection />
        <ProcessSection />
        <ProofSection />
        <CaseStudiesSection />
        <OfferSection />
        <InHouseSection />
        <CTASection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
};

// Main App
function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen bg-[#0B0F2F] noise-overlay">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-costa-primary text-white px-4 py-2 rounded-lg z-[100]">
          Aller au contenu principal
        </a>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/mentions-legales" element={<><Navigation /><MentionsLegales /><Footer /></>} />
          <Route path="/politique-de-confidentialite" element={<><Navigation /><PolitiqueConfidentialite /><Footer /></>} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
